import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  DEFAULT_SORT, DEFAULTS, SORTS, TRANSITIONS, bodyParticipation, describeScan, fetchOptions,
  formatDelta, isD9Kind, kindCode, kindLabel, kindTally, pairKey, pairsCarrying, pivotRows,
  reachableKeys, runScan, sortCommonalities, strongestPairFor,
} from '../utils/pivotCommonalities';

const MONO = { fontFamily: 'monospace', fontSize: 11.5 };
const HEAD = { fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', py: 0.5 };
const CELL = { fontSize: 11.5, py: 0.4, whiteSpace: 'nowrap' };

/**
 * The Pivot Planetary Commonalities study. ADMIN ONLY, and research rather than product.
 *
 * <h2>⚠ THREE VIEWS OF ONE SCAN, because the brief asks three different questions</h2>
 *
 * <pre>
 *   MATRIX   what RECURS across every pair        sortable; see below
 *   PAIRS    which two pivots agree most          ranked by BITS
 *   PIVOTS   what the detector actually found     chronological, with the separations
 * </pre>
 *
 * Tabs rather than one scrolling column: a thirty-year chart yields ~180 pivots, ~16,600 pairs
 * and a matrix of a few hundred keys, and stacked they are several screens of table in a dialog
 * that also has to leave the chart behind it visible.
 *
 * <h2>⚠ THE MATRIX KEEPS THE SERVER'S COUNT ORDER, and the FILTER is what makes it readable</h2>
 *
 * The server ranks by count, which is the brief's own question and is right: ordered by
 * improbability every one-off would be at the top, and a key seen once is by construction the
 * rarest thing in a run and the least like a commonality.
 *
 * <p>Measured on SPY, count-order turns out to be very nearly bits-order REVERSED — a D9 mutual
 * aspect fires on ~43% of pairs at 1.2 bits and holds 110 of 361 rows, so the brief's own
 * headline kinds sit below 220 rows of the weakest two. NO ORDERING FIXES THAT; see
 * `sortCommonalities` for the `count x bits` sort that was built for it, measured, and dropped
 * because it reproduces count-order exactly. The KIND chips are the instrument that works.
 *
 * <p>A PAIR is the opposite question: "which two pivots are most unusually alike" is exactly a
 * bits question, and there the D9 noise is a near-constant offset that does not move the order.
 *
 * <h2>Bits are COINCIDENCE, not confidence</h2>
 *
 * Nothing here measures a forward return, and the panel says so first rather than in a footnote.
 * Every other geometric study in this repository that produced a confident-looking figure turned
 * out to have no directional edge once it was measured against its own base rate.
 */
const PivotCommonalitiesDialog = ({
  open, onClose, isAdmin, token, symbol, onPaint, onClearMarks, onFocusDate,
}) => {
  const [window_, setWindow] = useState(DEFAULTS.window);
  const [minSeparation, setMinSeparation] = useState(DEFAULTS.minSeparation);
  const [orbDegrees, setOrbDegrees] = useState(DEFAULTS.orbDegrees);
  const [d9MinSpan, setD9MinSpan] = useState(DEFAULTS.d9MinSpan);
  const [pairLimit, setPairLimit] = useState(DEFAULTS.pairLimit);
  const [serverDefaults, setServerDefaults] = useState(null);

  const [state, setState] = useState('IDLE');
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [tab, setTab] = useState(0);
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedPair, setSelectedPair] = useState(null);
  const [expandedPivot, setExpandedPivot] = useState(null);
  const [paintNote, setPaintNote] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);
  // NULL means every kind, which is not the same as an empty array -- that is "none selected"
  // and must show an empty table rather than silently falling back to everything.
  const [kinds, setKinds] = useState(null);

  // Supersession: a slow scan must not land its results over a newer one's. A ref rather than
  // state, because it is read inside an async closure.
  const scanId = useRef(0);

  // ⚠ THROUGH REFS. The parent passes inline arrows -- which is how every dialog here is wired
  // -- so naming them in a dependency array would rebuild `run` on every render of the app.
  const paintRef = useRef(onPaint);
  paintRef.current = onPaint;
  const clearRef = useRef(onClearMarks);
  clearRef.current = onClearMarks;
  const focusRef = useRef(onFocusDate);
  focusRef.current = onFocusDate;

  useEffect(() => {
    if (!open || !isAdmin || !token || serverDefaults) return undefined;
    let cancelled = false;
    // SERVED rather than hardcoded, so a default changed in Java cannot leave this panel showing
    // a number the run does not use.
    fetchOptions(token)
      .then((opts) => {
        if (cancelled || !opts) return;
        setServerDefaults(opts);
        if (Number.isFinite(opts.window)) setWindow(opts.window);
        if (Number.isFinite(opts.minSeparation)) setMinSeparation(opts.minSeparation);
        if (Number.isFinite(opts.orbDegrees)) setOrbDegrees(opts.orbDegrees);
        if (Number.isFinite(opts.d9MinSpan)) setD9MinSpan(opts.d9MinSpan);
        if (Number.isFinite(opts.pairLimit)) setPairLimit(opts.pairLimit);
      })
      .catch(() => { /* the mirrored defaults are usable; a failed options call is not a failure */ });
    return () => { cancelled = true; };
  }, [open, isAdmin, token, serverDefaults]);

  const clearMarks = useCallback(() => {
    if (clearRef.current) clearRef.current();
    setSelectedPair(null);
    setPaintNote('');
  }, []);

  const paint = useCallback((pair, match) => {
    setSelectedPair(pair ? pairKey(pair) : null);
    if (paintRef.current) setPaintNote(paintRef.current(pair, match) || '');
  }, []);

  const run = useCallback(async () => {
    const mine = (scanId.current += 1);
    setError('');
    setReport(null);
    setSelectedKey(null);
    setSelectedPair(null);
    setExpandedPivot(null);
    setPaintNote('');
    setKinds(null);
    // CLEARED FIRST and unconditionally. A re-run that finds nothing -- or fails -- must take
    // the previous pair off the chart rather than leave it standing there looking current. The
    // panel is modeless, so the reader is looking at the canvas and not at the table.
    if (clearRef.current) clearRef.current();
    setState('RUNNING');
    try {
      const result = await runScan(token, {
        symbol,
        window: Number(window_),
        minSeparation: Number(minSeparation),
        orbDegrees: Number(orbDegrees),
        d9MinSpan: Number(d9MinSpan),
        pairLimit: Number(pairLimit),
      });
      if (mine !== scanId.current) return;
      setReport(result);
      setState('COMPLETE');
      /*
       * ⚠ THE STRONGEST PAIR IS PAINTED AT ONCE -- a reversal, on instruction.
       *
       * It shipped NOT painting, on the reasoning that a run produces up to four hundred pairs
       * and there is no single one the study is about, so painting the strongest would make the
       * chart answer a question nobody asked. That is still true of the CHOICE and was wrong
       * about the COST: the reader has to reach the chart through three gestures, and the
       * natural first one -- click the top matrix row -- lands on a dead row 118 times out of
       * 361 and paints nothing. A study whose output is a decorated chart must decorate it.
       *
       * <p>The strongest pair is a defensible default rather than an arbitrary one: it is the
       * first row of the Pairs tab, and the panel names what it drew.
       */
      const first = (result.pairs || [])[0];
      if (first && paintRef.current) {
        setSelectedPair(pairKey(first));
        setPaintNote(paintRef.current(first) || '');
      }
    } catch (e) {
      if (mine !== scanId.current) return;
      setError(e.message || String(e));
      setState('FAILED');
    }
  }, [token, symbol, window_, minSeparation, orbDegrees, d9MinSpan, pairLimit]);

  if (!isAdmin) return null;

  const busy = state === 'RUNNING';
  const pairs = (report && report.pairs) || [];
  const allCommonalities = (report && report.commonalities) || [];
  const commonalities = sortCommonalities(allCommonalities, sort, kinds);
  const tally = kindTally(report);
  // ONE scan per report rather than per row: a per-row answer is sixty pairs times three
  // hundred rows on every render.
  const reachable = reachableKeys(report);
  const deadRows = allCommonalities.filter((c) => !reachable.has(c.key)).length;
  const pivots = pivotRows(report);
  const bodies = bodyParticipation(report);
  const carrying = selectedKey ? pairsCarrying(report, selectedKey) : [];

  const pairRow = (pair, index, match) => {
    const selected = selectedPair === pairKey(pair);
    return (
      <TableRow key={`${pairKey(pair)}-${index}`} hover selected={selected}
        sx={{ cursor: 'pointer' }} onClick={() => paint(pair)}
        data-testid="pivot-pair-row">
        <TableCell sx={{ ...CELL, ...MONO }}>{pair.a.date}</TableCell>
        <TableCell sx={CELL}>
          <Chip size="small" label={pair.a.kind === 'HIGH' ? 'H' : 'L'}
            color={pair.a.kind === 'HIGH' ? 'error' : 'success'} variant="outlined"
            sx={{ height: 17, fontSize: 10 }} />
        </TableCell>
        <TableCell sx={{ ...CELL, ...MONO }}>{pair.b.date}</TableCell>
        <TableCell sx={CELL}>
          <Chip size="small" label={pair.b.kind === 'HIGH' ? 'H' : 'L'}
            color={pair.b.kind === 'HIGH' ? 'error' : 'success'} variant="outlined"
            sx={{ height: 17, fontSize: 10 }} />
        </TableCell>
        <TableCell sx={CELL}>{pair.transitionLabel || TRANSITIONS[pair.transition]}</TableCell>
        <TableCell align="right" sx={{ ...CELL, ...MONO }}>{pair.barSpan}</TableCell>
        <TableCell align="right" sx={{ ...CELL, ...MONO }}>{pair.matchCount}</TableCell>
        <TableCell align="right" sx={{ ...CELL, ...MONO }}>{pair.d9Count}</TableCell>
        <TableCell align="right" sx={{ ...CELL, ...MONO, fontWeight: 700 }}>
          {Number(pair.bits).toFixed(1)}
        </TableCell>
        <TableCell sx={{ ...CELL, ...MONO, color: 'text.secondary' }}>
          {match
            ? `${kindCode(match.kind)} ${match.from === match.to ? match.from : `${match.from}→${match.to}`}`
              + (Number.isFinite(match.deltaDeg) ? ` ${formatDelta(match.deltaDeg)}` : '')
            : (pair.matches || []).slice(0, 3).map((m) => kindCode(m.kind)).join(' ')}
        </TableCell>
      </TableRow>
    );
  };

  return (
    // MODELESS, like the analog scanner and the scanner candidates dialog, and for the same
    // reason: selecting a row marks two bars on the canvas BEHIND this panel, and a backdrop
    // would dim the one thing the gesture exists to show.
    <Dialog open={!!open} onClose={onClose} maxWidth="xl" fullWidth hideBackdrop
      sx={{ '& .MuiDialog-paper': { maxHeight: '90vh' } }}
      data-testid="pivot-commonalities-dialog">
      <DialogTitle sx={{ pb: 0.5, fontSize: 16, fontWeight: 700 }}>
        Pivot Planetary Commonalities Study
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
          Planetary recurrences measured strictly between {symbol || 'the loaded symbol'}&apos;s
          own price pivots — pivot to pivot, with no intervening bar read by any rule.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5 }}>
        {/* ⚠ FIRST, not in a footnote. */}
        <Alert severity="warning" sx={{ mb: 1.5, py: 0 }} data-testid="pivot-commonality-caveat">
          <strong>Research tool — nothing here measures a forward return.</strong> A row&apos;s
          BITS are how improbable that agreement is across the pairs this run actually compared:
          ten bits is about one pair in a thousand. That is a measure of COINCIDENCE within the
          scanned history, not a probability that anything follows a pivot, and not a claim that
          the geometry caused the turn. Every geometric study measured in this repository has
          come back with no directional edge against its own base rate.
        </Alert>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start', mb: 1 }}>
          <TextField size="small" type="number" label="Pivot window (bars)" value={window_}
            onChange={(e) => setWindow(e.target.value)}
            sx={{ width: 150 }} InputLabelProps={{ shrink: true }}
            inputProps={{ min: 1, max: 200, 'data-testid': 'pivot-window' }} />
          <TextField size="small" type="number" label="Min separation" value={minSeparation}
            onChange={(e) => setMinSeparation(e.target.value)}
            sx={{ width: 140 }} InputLabelProps={{ shrink: true }}
            inputProps={{ min: 1, max: 500, 'data-testid': 'pivot-min-separation' }} />
          <TextField size="small" type="number" label="Orb (degrees)" value={orbDegrees}
            onChange={(e) => setOrbDegrees(e.target.value)}
            sx={{ width: 130 }} InputLabelProps={{ shrink: true }}
            inputProps={{ min: 0.05, max: 15, step: 0.25, 'data-testid': 'pivot-orb' }} />
          <TextField size="small" type="number" label="D9 min span (bars)" value={d9MinSpan}
            onChange={(e) => setD9MinSpan(e.target.value)}
            sx={{ width: 155 }} InputLabelProps={{ shrink: true }}
            inputProps={{ min: 0, 'data-testid': 'pivot-d9-span' }} />
          <TextField size="small" type="number" label="Pairs kept" value={pairLimit}
            onChange={(e) => setPairLimit(e.target.value)}
            sx={{ width: 125 }} InputLabelProps={{ shrink: true }}
            inputProps={{ min: 1, max: 400, 'data-testid': 'pivot-pair-limit' }} />
          <Button variant="contained" size="small" onClick={run} disabled={busy || !symbol}
            data-testid="pivot-commonality-run">
            {busy ? 'Scanning…' : 'Run study'}
          </Button>
          <Button size="small" onClick={clearMarks} disabled={!report}
            data-testid="pivot-commonality-clear">
            Clear chart marks
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}

        {report && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}
            data-testid="pivot-commonality-summary">
            {describeScan(report)}
          </Typography>
        )}

        {paintNote && (
          <Alert severity="info" sx={{ mb: 1, py: 0 }} data-testid="pivot-commonality-paint-note">
            {paintNote}
            <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
              A triangle sits above each HIGH and below each LOW, labelled A and B, with a dashed
              vertical on both bars. Hover either mark for the whole pair. Select another row —
              here or on the Pairs tab — to move the marks.
            </Typography>
          </Alert>
        )}

        {report && (
          <>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ minHeight: 34, mb: 1 }}
              data-testid="pivot-commonality-tabs">
              <Tab sx={{ minHeight: 34, fontSize: 12 }}
                label={`Commonalities matrix (${allCommonalities.length})`} />
              <Tab sx={{ minHeight: 34, fontSize: 12 }}
                label={`Strongest pairs (${pairs.length})`} />
              <Tab sx={{ minHeight: 34, fontSize: 12 }}
                label={`Pivots (${pivots.length})`} />
            </Tabs>

            {/* ------------------------------------------------ 1. the matrix */}
            {tab === 0 && (
              <>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center',
                  mb: 0.75 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>Rank by</Typography>
                  {Object.keys(SORTS).map((k) => (
                    <Chip key={k} size="small" clickable label={SORTS[k].label}
                      data-testid={`pivot-sort-${k}`}
                      color={sort === k ? 'primary' : 'default'}
                      variant={sort === k ? 'filled' : 'outlined'}
                      onClick={() => setSort(k)}
                      sx={{ height: 20, fontSize: 10.5 }} />
                  ))}
                  <Box sx={{ width: 12 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>Kinds</Typography>
                  {tally.map((t) => {
                    const on = kinds === null || kinds.includes(t.kind);
                    return (
                      <Chip key={t.kind} size="small" clickable title={kindLabel(t.kind)}
                        data-testid={`pivot-kind-${kindCode(t.kind)}`}
                        label={`${kindCode(t.kind)} ${t.keys}`}
                        color={on ? (isD9Kind(t.kind) ? 'secondary' : 'primary') : 'default'}
                        variant={on ? 'filled' : 'outlined'}
                        onClick={() => setKinds((prev) => {
                          const base = prev === null ? tally.map((x) => x.kind) : prev;
                          return base.includes(t.kind)
                            ? base.filter((x) => x !== t.kind) : [...base, t.kind];
                        })}
                        sx={{ height: 20, fontSize: 10.5, opacity: on ? 1 : 0.5 }} />
                    );
                  })}
                  {kinds !== null && (
                    <Chip size="small" clickable label="all" onClick={() => setKinds(null)}
                      data-testid="pivot-kind-all" sx={{ height: 20, fontSize: 10.5 }} />
                  )}
                </Box>
                {/* ⚠ THE DEFAULT IS NOT THE SERVER'S ORDER, and the reason is measured.
                    Count-order is very nearly bits-order reversed: on SPY the two weakest kinds
                    hold 220 of 361 rows, so the brief's own headline questions sit below all of
                    them. The server keeps ranking by count — that answers a real question and
                    is what the brief asks for — and this picks a different one by default. */}
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                  {sort === 'count'
                    ? 'Ranked by how OFTEN each recurrence appears — the brief\'s own question, '
                      + 'and the ordering the server returns.'
                    : (sort === 'bits'
                      ? 'Ranked by RARITY. A key seen once is the rarest thing in the run and the '
                        + 'least like a commonality, so read the count column beside it.'
                      : 'Ranked by TOTAL INFORMATION (count × bits). A D9 mutual aspect fires on '
                        + 'about 43% of all pairs and is worth 1.2 bits, so by frequency alone it '
                        + 'buries every longitude return and transfer in the table.')}
                  {' '}Navamsha rows are measured against the {report.d9PairsEvaluated} pairs whose
                  span reached {report.d9MinSpan} bars; every other row against all
                  {' '}{report.pairsEvaluated}.
                  {' '}<strong>Select a row to mark its strongest pair on the chart.</strong>
                  {deadRows > 0 && (
                    <>
                      {' '}The {deadRows} dimmed rows are not reachable: each returned pair
                      carries its strongest matches only, so the weakest kinds are cut from
                      every one of them and have no pair to mark.
                    </>
                  )}
                </Typography>
                <TableContainer sx={{ maxHeight: '38vh' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={HEAD}>Type</TableCell>
                        <TableCell sx={HEAD}>Bodies</TableCell>
                        <TableCell align="right" sx={HEAD}>Count</TableCell>
                        <TableCell align="right" sx={HEAD}>Share</TableCell>
                        <TableCell align="right" sx={HEAD}>Bits</TableCell>
                        <TableCell align="right" sx={HEAD}>Motion held</TableCell>
                        <TableCell align="right" sx={HEAD}>H→H</TableCell>
                        <TableCell align="right" sx={HEAD}>H→L</TableCell>
                        <TableCell align="right" sx={HEAD}>L→H</TableCell>
                        <TableCell align="right" sx={HEAD}>L→L</TableCell>
                        <TableCell sx={HEAD}>Example</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {commonalities.map((c) => {
                        const live = reachable.has(c.key);
                        return (
                        <React.Fragment key={c.key}>
                          {/* ⚠ ONE CLICK PAINTS, and a DEAD row says so before it is clicked.
                              Measured on SPY: 173 of 361 rows carry no pair that came back, and
                              the first reachable one is at index 118 -- so the natural gesture,
                              clicking the top row, expanded to nothing and blamed the pair
                              limit. Dimmed and marked here, and a second gesture is no longer
                              needed for the ones that work. */}
                          <TableRow hover selected={selectedKey === c.key}
                            sx={{ cursor: live ? 'pointer' : 'default', opacity: live ? 1 : 0.45 }}
                            data-testid="pivot-commonality-row"
                            data-reachable={live ? 'yes' : 'no'}
                            onClick={() => {
                              setSelectedKey(selectedKey === c.key ? null : c.key);
                              // Painting the STRONGEST carrier rather than waiting for a chip:
                              // the pairs arrive bits-descending, so the first is the strongest
                              // and the chart agrees with the Pairs tab by construction.
                              const hit = live ? strongestPairFor(report, c.key) : null;
                              if (hit) paint(hit.pair, hit.match);
                            }}>
                            <TableCell sx={CELL}>
                              <Chip size="small" label={kindCode(c.kind)} title={kindLabel(c.kind)}
                                color={isD9Kind(c.kind) ? 'secondary' : 'primary'}
                                variant={isD9Kind(c.kind) ? 'filled' : 'outlined'}
                                sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                            </TableCell>
                            <TableCell sx={{ ...CELL, fontWeight: 600 }}>
                              {c.from === c.to ? c.from : `${c.from} → ${c.to}`}
                            </TableCell>
                            <TableCell align="right" sx={{ ...CELL, ...MONO, fontWeight: 700 }}>
                              {c.count}
                            </TableCell>
                            <TableCell align="right" sx={{ ...CELL, ...MONO }}>
                              {(c.share * 100).toFixed(1)}%
                            </TableCell>
                            <TableCell align="right" sx={{ ...CELL, ...MONO }}>
                              {Number(c.bits).toFixed(1)}
                            </TableCell>
                            <TableCell align="right" sx={{ ...CELL, ...MONO }}>
                              {c.motionHeld}/{c.count}
                            </TableCell>
                            {['HIGH_HIGH', 'HIGH_LOW', 'LOW_HIGH', 'LOW_LOW'].map((t) => (
                              <TableCell key={t} align="right" sx={{ ...CELL, ...MONO }}>
                                {(c.byTransition && c.byTransition[t]) || 0}
                              </TableCell>
                            ))}
                            <TableCell sx={{ ...CELL, ...MONO, color: 'text.secondary' }}>
                              {(c.examples || [])[0] || ''}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ p: 0, border: 0 }} colSpan={11}>
                              <Collapse in={selectedKey === c.key} unmountOnExit>
                                <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                    {kindLabel(c.kind)} — {c.count} occurrences across the run.
                                    {/* ⚠ THE CAP IS NAMED. The matrix counts over every pair
                                        walked; only `pairLimit` pairs came back, so a key with a
                                        count of 30 can be reachable on six rows here. Unsaid,
                                        that reads as a bug. */}
                                    {' '}{carrying.length} of them are among the {pairs.length} pairs
                                    kept — the matrix counts across all {report.pairsEvaluated}.
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {carrying.map(({ pair, match }) => (
                                      <Chip key={pairKey(pair)} size="small" clickable
                                        color={selectedPair === pairKey(pair) ? 'warning' : 'default'}
                                        variant={selectedPair === pairKey(pair) ? 'filled' : 'outlined'}
                                        onClick={() => paint(pair, match)}
                                        data-testid="pivot-commonality-pair-chip"
                                        label={`${pair.a.date} → ${pair.b.date}`
                                          + (Number.isFinite(match.deltaDeg)
                                            ? ` (${formatDelta(match.deltaDeg)})` : '')}
                                        sx={{ height: 20, fontSize: 10.5, ...MONO }} />
                                    ))}
                                    {carrying.length === 0 && (
                                      <Typography variant="caption" color="text.secondary"
                                        data-testid="pivot-commonality-unreachable">
                                        {/* ⚠ THE OLD WORDING BLAMED THE WRONG CAP. "None of its
                                            pairs scored high enough" is false for a key present
                                            in 7,282 of 16,653 pairs -- it is in nearly all of
                                            them. What hides it is the PER-PAIR cap: each pair
                                            returns its 14 strongest matches and this kind is
                                            the weakest, so it is truncated out of every one.
                                            Raising "Pairs kept" would not have helped. */}
                                        Not reachable on any returned pair. Each pair comes back
                                        with its {report.pairs[0]
                                          ? report.pairs[0].matches.length : 14} strongest
                                        matches only, and at {Number(c.bits).toFixed(1)} bits
                                        this one is cut from every single one of them &mdash;
                                        so there is no pair here to mark on the chart.
                                        {isD9Kind(c.kind) && ' Every Navamsha mutual-aspect row '
                                          + 'is in this position: the kind fires on about 43% '
                                          + 'of pairs and is the weakest in the study.'}
                                      </Typography>
                                    )}
                                  </Stack>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                        );
                      })}
                      {commonalities.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={11} sx={{ ...CELL, color: 'text.secondary' }}>
                            {/* THE TWO EMPTIES ARE DIFFERENT FACTS. "the filter hid everything"
                                and "the study found nothing" look identical as a blank table. */}
                            {allCommonalities.length === 0
                              ? 'No recurrence cleared the orb across any pair.'
                              : `All ${allCommonalities.length} rows are hidden by the kind filter.`}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {bodies.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Participating bodies
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {bodies.map((b) => (
                        <Chip key={b.body} size="small" variant="outlined"
                          data-testid="pivot-body-chip"
                          label={`${b.body} · ${b.count}`}
                          sx={{ height: 20, fontSize: 10.5 }} />
                      ))}
                    </Stack>
                  </Box>
                )}
              </>
            )}

            {/* ------------------------------------------------ 2. the pairs */}
            {tab === 1 && (
              <>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                  The {pairs.length} most unusually alike pivot pairs of the {report.pairsEvaluated}
                  {' '}compared, ranked by total bits. Select one to mark both bars on the chart.
                </Typography>
                <TableContainer sx={{ maxHeight: '48vh' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={HEAD}>Pivot A</TableCell>
                        <TableCell sx={HEAD} />
                        <TableCell sx={HEAD}>Pivot B</TableCell>
                        <TableCell sx={HEAD} />
                        <TableCell sx={HEAD}>Transition</TableCell>
                        <TableCell align="right" sx={HEAD}>Span</TableCell>
                        <TableCell align="right" sx={HEAD}>Matches</TableCell>
                        <TableCell align="right" sx={HEAD}>D9</TableCell>
                        <TableCell align="right" sx={HEAD}>Bits</TableCell>
                        <TableCell sx={HEAD}>Top</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pairs.map((p, i) => pairRow(p, i, null))}
                      {pairs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={10} sx={{ ...CELL, color: 'text.secondary' }}>
                            No pair showed a commonality.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* ------------------------------------------------ 3. the pivots */}
            {tab === 2 && (
              <>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                  Every pivot the {report.window}-bar window validated, in order, with the
                  separation from the one before it — the {report.minSeparation}-bar constraint is
                  checkable row by row. Select a row for its planetary snapshot.
                </Typography>
                <TableContainer sx={{ maxHeight: '48vh' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={HEAD}>#</TableCell>
                        <TableCell sx={HEAD}>Date</TableCell>
                        <TableCell sx={HEAD}>Kind</TableCell>
                        <TableCell align="right" sx={HEAD}>Price</TableCell>
                        <TableCell align="right" sx={HEAD}>Bar</TableCell>
                        <TableCell align="right" sx={HEAD}>Bars since previous</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pivots.map((p) => (
                        <React.Fragment key={`${p.date}-${p.barIndex}`}>
                          <TableRow hover selected={expandedPivot === p.barIndex}
                            sx={{ cursor: 'pointer' }} data-testid="pivot-summary-row"
                            onClick={() => {
                              setExpandedPivot(expandedPivot === p.barIndex ? null : p.barIndex);
                              if (focusRef.current) focusRef.current(p.date);
                            }}>
                            <TableCell sx={{ ...CELL, ...MONO, color: 'text.secondary' }}>
                              {p.index + 1}
                            </TableCell>
                            <TableCell sx={{ ...CELL, ...MONO }}>{p.date}</TableCell>
                            <TableCell sx={CELL}>
                              <Chip size="small" label={p.kind === 'HIGH' ? 'High' : 'Low'}
                                color={p.kind === 'HIGH' ? 'error' : 'success'} variant="outlined"
                                sx={{ height: 18, fontSize: 10 }} />
                            </TableCell>
                            <TableCell align="right" sx={{ ...CELL, ...MONO }}>
                              {Number(p.price).toFixed(2)}
                            </TableCell>
                            <TableCell align="right" sx={{ ...CELL, ...MONO }}>{p.barIndex}</TableCell>
                            <TableCell align="right" sx={{ ...CELL, ...MONO }}>
                              {p.barsSincePrevious === null ? '—' : p.barsSincePrevious}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ p: 0, border: 0 }} colSpan={6}>
                              <Collapse in={expandedPivot === p.barIndex} unmountOnExit>
                                <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={HEAD}>Body</TableCell>
                                        <TableCell align="right" sx={HEAD}>Tropical</TableCell>
                                        <TableCell align="right" sx={HEAD}>Sidereal</TableCell>
                                        <TableCell sx={HEAD}>Motion</TableCell>
                                        <TableCell sx={HEAD}>D9 sign</TableCell>
                                        <TableCell align="right" sx={HEAD}>Pada</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(p.bodies || []).map((b) => (
                                        <TableRow key={b.body} data-testid="pivot-body-row">
                                          <TableCell sx={{ ...CELL, fontWeight: 600 }}>{b.body}</TableCell>
                                          <TableCell align="right" sx={{ ...CELL, ...MONO }}>
                                            {Number(b.tropical).toFixed(2)}°
                                          </TableCell>
                                          <TableCell align="right" sx={{ ...CELL, ...MONO }}>
                                            {Number(b.sidereal).toFixed(2)}°
                                          </TableCell>
                                          <TableCell sx={CELL}>
                                            {b.retrograde
                                              ? <Chip size="small" label="R" color="warning"
                                                sx={{ height: 17, fontSize: 10 }} />
                                              : <Typography variant="caption" color="text.secondary">D</Typography>}
                                          </TableCell>
                                          <TableCell sx={CELL}>{b.d9SignName}</TableCell>
                                          <TableCell align="right" sx={{ ...CELL, ...MONO }}>
                                            {b.d9Pada}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                      {pivots.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ ...CELL, color: 'text.secondary' }}>
                            The window validated no pivots on this series.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}

        {!report && !busy && !error && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Press <strong>Run study</strong> to detect {symbol || 'this symbol'}&apos;s pivots and
            compare every pair of them.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

PivotCommonalitiesDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  token: PropTypes.string,
  symbol: PropTypes.string,
  onPaint: PropTypes.func,
  onClearMarks: PropTypes.func,
  onFocusDate: PropTypes.func,
};

export default PivotCommonalitiesDialog;
